import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Avatar,
  Chip,
} from "@mui/material";
import { Share, Business } from "@mui/icons-material";

/**
 * QuoteHeader - Displays company branding and quote information
 * Mobile-first responsive design with company logo and quote details
 */
export const QuoteHeader = ({ quote, onShare }) => {
  const BUSINESS_NAME =
    import.meta.env.VITE_BUSINESS_NAME || "Bubakii (Pty) Ltd";

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-ZA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      sent: "info",
      negotiating: "primary",
      approved: "success",
      accepted: "success",
      rejected: "error",
      expired: "default",
    };
    return colors[status] || "default";
  };

  return (
    <Card
      sx={(theme) => ({
        mb: 2,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.getContrastText(theme.palette.primary.main),
      })}
    >
      <CardContent sx={{ pb: 2 }}>
        {/* Company Branding */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={(theme) => ({
                bgcolor: theme.palette.common.white,
                color: theme.palette.primary.main,
                width: 48,
                height: 48,
                fontSize: "1.5rem",
                fontWeight: "bold",
              })}
            >
              <Business />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: "inherit" }}
              >
                {BUSINESS_NAME}
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.9, color: "inherit" }}
              >
                Professional Moving Services
              </Typography>
            </Box>
          </Box>

          {onShare && (
            <IconButton
              onClick={onShare}
              sx={{
                color: "inherit",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            >
              <Share />
            </IconButton>
          )}
        </Box>

        {/* Quote Information */}
        <Box display="flex" flexDirection="column" gap={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: "inherit" }}
            >
              Quote #{quote?.id}
            </Typography>
            <Chip
              label={quote?.status?.toUpperCase() || "UNKNOWN"}
              color={getStatusColor(quote?.status)}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "inherit",
                fontWeight: "bold",
              }}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" sx={{ opacity: 0.9, color: "inherit" }}>
              Created: {formatDate(quote?.createdAt)}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, color: "inherit" }}>
              Last Updated: {formatDate(quote?.lastUpdated)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
